/**
 * HSL ↔ HEX conversion utilities for the custom-color picker.
 * HSL is stored as a three-part string ("H S% L%") so it can be plugged
 * straight into Tailwind's `hsl(var(--token))` references without parsing.
 */

export interface Hsl {
  h: number;
  s: number;
  l: number;
}

export function hslToString({ h, s, l }: Hsl): string {
  return `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`;
}

export function parseHslString(value: string): Hsl | null {
  const m = value.trim().match(/^(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%$/);
  if (!m) return null;
  return { h: Number(m[1]), s: Number(m[2]), l: Number(m[3]) };
}

export function hexToHsl(hex: string): Hsl | null {
  let clean = hex.trim().replace('#', '');
  if (clean.length === 3) {
    clean = clean
      .split('')
      .map((c) => c + c)
      .join('');
  }
  if (clean.length !== 6) return null;
  const r = parseInt(clean.slice(0, 2), 16) / 255;
  const g = parseInt(clean.slice(2, 4), 16) / 255;
  const b = parseInt(clean.slice(4, 6), 16) / 255;
  if ([r, g, b].some(Number.isNaN)) return null;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let h = 0;
  let s = 0;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }
    h *= 60;
  }
  return { h, s: s * 100, l: l * 100 };
}

export function hslToHex(input: Hsl | string): string {
  const hsl = typeof input === 'string' ? parseHslString(input) : input;
  if (!hsl) return '#000000';
  const { h, s, l } = hsl;
  const sn = s / 100;
  const ln = l / 100;
  const c = (1 - Math.abs(2 * ln - 1)) * sn;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = ln - c / 2;
  let r = 0;
  let g = 0;
  let b = 0;
  if (h < 60) {
    r = c;
    g = x;
  } else if (h < 120) {
    r = x;
    g = c;
  } else if (h < 180) {
    g = c;
    b = x;
  } else if (h < 240) {
    g = x;
    b = c;
  } else if (h < 300) {
    r = x;
    b = c;
  } else {
    r = c;
    b = x;
  }
  const toHex = (v: number) =>
    Math.round((v + m) * 255)
      .toString(16)
      .padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

/**
 * Pick a readable foreground color (white vs near-black) given an HSL background.
 * Uses WCAG-ish luminance approximation — good enough for theme tokens.
 */
export function pickForeground(bg: Hsl): string {
  return bg.l > 55 ? '222 47% 11%' : '210 40% 98%';
}

/** Derive a soft accent tint from a primary HSL by raising lightness. */
export function deriveAccent(primary: Hsl, isDark: boolean): { bg: string; fg: string } {
  if (isDark) {
    return {
      bg: hslToString({ h: primary.h, s: Math.max(15, primary.s * 0.35), l: 20 }),
      fg: '210 40% 98%',
    };
  }
  return {
    bg: hslToString({ h: primary.h, s: Math.min(100, primary.s + 10), l: 95 }),
    fg: hslToString({ h: primary.h, s: primary.s, l: Math.max(20, primary.l - 5) }),
  };
}
